import { useState, useEffect, useRef, useCallback } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { Loader2, TrendingUp, Zap } from 'lucide-react';
import { useUserContext } from '../contexts/UserContext';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import QRScanner from '../components/QRScanner';
import StaffAttendanceModal from '../components/StaffAttendanceModal';
import MemberAttendanceModal from '../components/MemberAttendanceModal';
import PulseTimeline from '../components/PulseTimeline';
import TimeRingHeatmap from '../components/TimeRingHeatmap';
import AttendanceBarChart, { DailyTrendData } from '../components/AttendanceBarChart';
import type { HourlyData } from '../components/PulseTimeline';

interface AttendanceState {
  status: 'idle' | 'scanning' | 'processing' | 'success' | 'blocked' | 'error';
  message: string;
  action?: 'checkin' | 'checkout';
  memberInfo?: { name: string; phone: string; status: string; expiryDate?: string };
  blockReason?: 'trial_expired' | 'lead' | 'invalid_qr' | 'membership_expired' | 'other';
}

// ─── Shared hour label formatter ─────────────────────────────────────────────

function fmtHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

// ─── Hour select dropdown ─────────────────────────────────────────────────────

function HourSelect({ value, onChange, min = 0, max = 23 }: { value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  const options: number[] = [];
  for (let h = min; h <= max; h++) options.push(h);
  return (
    <select
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 text-zinc-950 text-[10px] font-black uppercase tracking-wide rounded-xl px-2.5 py-1.5 appearance-none cursor-pointer outline-none focus:border-[#AAD816]/40 focus:ring-1 focus:ring-[#AAD816]/20 transition-all font-sans"
      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='rgba(0,0,0,0.3)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2.5' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center', backgroundSize: '10px', paddingRight: '22px' }}
    >
      {options.map(h => (
        <option key={h} value={h} className="bg-white text-zinc-950">
          {fmtHour(h)}
        </option>
      ))}
    </select>
  );
}

export default function AttendancePage() {
  const { hasPermission, userContext } = useUserContext();
  const [attendanceState, setAttendanceState] = useState<AttendanceState>({ status: 'idle', message: 'Ready to scan' });
  const [isScanning, setIsScanning] = useState(false);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string>('');
  const [scannerKey, setScannerKey] = useState(0);

  const [recentCheckIns, setRecentCheckIns] = useState<any[]>([]);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [branches, setBranches] = useState<any[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(true);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('all');
  const [isBranchMenuOpen, setIsBranchMenuOpen] = useState(false);
  const [hourlyData, setHourlyData] = useState<HourlyData[]>([]);
  const [selectedHour, setSelectedHour] = useState<number | undefined>();
  const [todayStats, setTodayStats] = useState({ total: 0, unique: 0, live: 0 });
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [rangeStart, setRangeStart] = useState(6);   // default 6 AM
  const [rangeEnd, setRangeEnd] = useState(21);      // default 9 PM
  const [dailyTrendData, setDailyTrendData] = useState<DailyTrendData[]>([]);
  const [isRecentLoaded, setIsRecentLoaded] = useState(false);
  const [isLoadingHourly, setIsLoadingHourly] = useState(true);
  const [isLoadingTrends, setIsLoadingTrends] = useState(true);
  const [avgHourlyData, setAvgHourlyData] = useState<number[]>(Array(24).fill(0));
  const [isLoadingAvgHourly, setIsLoadingAvgHourly] = useState(true);
  const [avgTotalDays, setAvgTotalDays] = useState(0);

  const lastScannedDataRef = useRef<string | null>(null);
  const lastScanTimeRef = useRef<number>(0);

  const canManageAttendance = hasPermission('log_attendance');
  const currentRole = userContext?.roles?.[0];
  const canUseStaffAttendance = currentRole?.plan?.features?.staff_attendance === true;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // ─── Data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!canManageAttendance) return;
    const fetchBranches = async () => {
      setIsLoadingBranches(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`${API_BASE_URL}/api/attendance/branches`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (res.ok) {
          const d = await res.json();
          setBranches(d.data || []);
        }
      } catch { /* silently ignore */ } finally {
        setIsLoadingBranches(false);
      }
    };
    fetchBranches();
  }, [canManageAttendance]);

    const fetchHourlyData = useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const params = selectedBranchId !== 'all' ? `?branchId=${selectedBranchId}` : '';
      
      // Fetch both hourly and general stats for live count
      const [hourlyRes, statsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/attendance/hourly${params}`, { headers: { 'Authorization': `Bearer ${session.access_token}` } }),
        fetch(`${API_BASE_URL}/api/attendance/stats${params}`, { headers: { 'Authorization': `Bearer ${session.access_token}` } })
      ]);

      if (hourlyRes.ok && statsRes.ok) {
        const hData = await hourlyRes.json();
        const sData = await statsRes.json();
        setHourlyData(hData.data || []);
        setTodayStats({ 
          total: sData.data?.total || 0, 
          unique: sData.data?.unique || 0,
          live: sData.data?.live || 0
        });
      }
    } catch { /* silently ignore */ } finally {
      setIsLoadingHourly(false);
      setIsLoadingStats(false);
    }
  }, [selectedBranchId]);

  const fetchDailyTrends = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const params = selectedBranchId !== 'all' ? `?branchId=${selectedBranchId}` : '';
      const res = await fetch(`${API_BASE_URL}/api/attendance/daily-trends${params}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setDailyTrendData(d.data || []);
      }
    } catch { /* silently ignore */ } finally {
      setIsLoadingTrends(false);
    }
  }, [selectedBranchId]);

  const fetchHourlyAverage = useCallback(async () => {
    setIsLoadingAvgHourly(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const params = selectedBranchId !== 'all' ? `?branchId=${selectedBranchId}` : '';
      const res = await fetch(`${API_BASE_URL}/api/attendance/hourly-average${params}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const d = await res.json();
        const payload = d.data;
        if (selectedBranchId === 'all') {
          setAvgHourlyData(payload.allBranches || Array(24).fill(0));
          const maxDays = Math.max(0, ...(payload.branches || []).map((b: any) => b.totalDays));
          setAvgTotalDays(maxDays);
        } else {
          const branch = (payload.branches || []).find((b: any) => b.branchId === selectedBranchId);
          setAvgHourlyData(branch?.hourlyData || Array(24).fill(0));
          setAvgTotalDays(branch?.totalDays || 0);
        }
      }
    } catch { /* silently ignore */ } finally {
      setIsLoadingAvgHourly(false);
    }
  }, [selectedBranchId]);

  const fetchRecentCheckIns = useCallback(async () => {
    if (!isRecentLoaded) return;
    setIsLoadingRecent(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const params = selectedBranchId !== 'all' ? `&branchId=${selectedBranchId}` : '';
      const res = await fetch(`${API_BASE_URL}/api/attendance/recent?limit=10${params}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const d = await res.json();
        setRecentCheckIns(d.data || []);
      }
    } catch { /* silently ignore */ } finally {
      setIsLoadingRecent(false);
    }
  }, [selectedBranchId, isRecentLoaded]);

  useEffect(() => {
    if (!canManageAttendance) return;
    setIsLoadingHourly(true);
    setIsLoadingTrends(true);
    fetchHourlyData();
    fetchDailyTrends();
    fetchHourlyAverage();
  }, [canManageAttendance, fetchHourlyData, fetchDailyTrends, fetchHourlyAverage, selectedBranchId]);

  useEffect(() => {
    if (canManageAttendance && isRecentLoaded) {
      fetchRecentCheckIns();
    }
  }, [canManageAttendance, isRecentLoaded, fetchRecentCheckIns]);

  // Refresh data every 2 minutes
  useEffect(() => {
    if (!canManageAttendance) return;
    const interval = setInterval(() => { 
      fetchHourlyData(); 
      if (isRecentLoaded) fetchRecentCheckIns(); 
    }, 120_000);
    return () => clearInterval(interval);
  }, [canManageAttendance, fetchHourlyData, fetchRecentCheckIns, isRecentLoaded]);

  // ─── Audio ──────────────────────────────────────────────────────────────────

  const playSuccessSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sine'; osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.5);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5);
    } catch { /* ignore */ }
  }, []);

  const playErrorSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'square'; osc.frequency.setValueAtTime(150, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(50, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.2);
    } catch { /* ignore */ }
  }, []);

  // ─── Camera ─────────────────────────────────────────────────────────────────

  const stopAllVideoStreams = useCallback(() => {
    document.querySelectorAll('video').forEach(v => {
      const s = v.srcObject as MediaStream | null;
      s?.getTracks().forEach(t => { t.stop(); t.enabled = false; });
      v.srcObject = null; v.pause();
    });
  }, []);

  useEffect(() => { if (!isScanning) stopAllVideoStreams(); }, [isScanning, stopAllVideoStreams]);

  const getAvailableCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(d => d.kind === 'videoinput');
      setAvailableCameras(cameras);
      if (cameras.length > 0 && !selectedCameraId) {
        if (isMobile) {
          const back = cameras.find(d => /back|environment|rear/i.test(d.label));
          setSelectedCameraId(back?.deviceId || cameras[cameras.length - 1].deviceId);
        } else {
          setSelectedCameraId(cameras[0].deviceId);
        }
      }
      return cameras;
    } catch { return []; }
  }, [selectedCameraId, isMobile]);

  const startScanning = useCallback(async () => {
    if (!canManageAttendance) { toast.error('No permission'); return; }
    lastScannedDataRef.current = null;
    lastScanTimeRef.current = 0;
    setScannerKey(prev => prev + 1);
    setIsScanning(true);
    setAttendanceState({ status: 'scanning', message: 'Initializing camera…' });
    const cameras = await getAvailableCameras();
    if (cameras.length === 0) {
      setAttendanceState({ status: 'error', message: 'No camera found.' });
      setIsScanning(false);
    }
  }, [canManageAttendance, getAvailableCameras]);

  const stopScanning = useCallback(() => {
    setIsScanning(false);
    stopAllVideoStreams();
    setAttendanceState({ status: 'idle', message: 'Ready to scan' });
  }, [stopAllVideoStreams]);

  // ─── Attendance processing ───────────────────────────────────────────────────

  const processAttendance = useCallback(async (phone: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      const branchId = selectedBranchId !== 'all' ? selectedBranchId : undefined;
      const body = { phone, ...(branchId && { branchId }) };
      const res = await fetch(`${API_BASE_URL}/api/attendance/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify(body)
      });
      const d = await res.json();
      if (res.ok || res.status === 409) {
        const action = d.action === 'checkout' ? 'checkout' : 'checkin';
        setAttendanceState({ status: 'success', message: res.status === 409 ? 'Already checked in' : 'Recorded!', action, memberInfo: d.data?.member });
        playSuccessSound();
        fetchRecentCheckIns();
        fetchHourlyData();
      } else if (d.blocked) {
        let blockReason: AttendanceState['blockReason'] = 'other';
        if (res.status === 404) blockReason = 'invalid_qr';
        else if (/lead/i.test(d.message)) blockReason = 'lead';
        else if (/trial/i.test(d.message)) blockReason = 'trial_expired';
        else if (/expired|membership/i.test(d.message)) blockReason = 'membership_expired';
        setAttendanceState({ status: 'blocked', message: d.message || 'Access blocked', blockReason, memberInfo: d.member ? { name: d.member.name, phone, status: d.member.status, expiryDate: d.member.expiryDate } : { name: 'Unknown', phone, status: 'blocked' } });
        playErrorSound();
      } else {
        throw new Error(d.error || 'Failed');
      }
    } catch {
      setAttendanceState({ status: 'error', message: 'Network error—please retry' });
      playErrorSound();
    }
  }, [selectedBranchId, playSuccessSound, playErrorSound, fetchRecentCheckIns, fetchHourlyData]);

  const handleQRCodeDetected = useCallback(async (qrData: string) => {
    const now = Date.now();
    if (qrData === lastScannedDataRef.current && now - lastScanTimeRef.current < 3000) return;
    lastScannedDataRef.current = qrData;
    lastScanTimeRef.current = now;
    setIsScanning(false);
    stopAllVideoStreams();
    setAttendanceState({ status: 'processing', message: 'Verifying…' });
    await processAttendance(qrData.trim());
    stopAllVideoStreams();
  }, [processAttendance, stopAllVideoStreams]);

  const handleCameraSwitch = () => {
    const idx = availableCameras.findIndex(c => c.deviceId === selectedCameraId);
    const next = availableCameras[(idx + 1) % availableCameras.length];
    setSelectedCameraId(next.deviceId);
    if (isScanning) { stopScanning(); setTimeout(() => startScanning(), 100); }
  };

  // ─── Computed values ─────────────────────────────────────────────────────────

  const peakEntry = hourlyData.reduce((best, d) => d.value > best.value ? d : best, { hour: 0, value: 0 });
  const peakHourLabel = peakEntry.value > 0
    ? (peakEntry.hour === 12 ? '12 PM' : peakEntry.hour < 12 ? `${peakEntry.hour} AM` : `${peakEntry.hour - 12} PM`)
    : '—';
  const selectedBranchName = selectedBranchId === 'all'
    ? 'All Branches'
    : (branches.find(b => b.id === selectedBranchId)?.displayName || branches.find(b => b.id === selectedBranchId)?.name || 'Branch');

  // ─── Access guard ─────────────────────────────────────────────────────────────

  if (!canManageAttendance) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-zinc-950 mb-2">Access Denied</h1>
          <p className="text-zinc-400">You don't have permission to manage attendance.</p>
        </div>
      </div>
    );
  }

  // ─── TODO: Clean up Member scan logic below once confirmed it's no longer needed ───
  const ScannerOverlay = () => (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col overflow-hidden font-sans select-none">
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-50 p-4 flex justify-between items-start pointer-events-none">
        <div className="pointer-events-auto">
          <div className="bg-white/5 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-[#AAD816] rounded-full shadow-[0_0_8px_rgba(170,216,22,0.8)]" />
            <span className="text-white text-[10px] font-black uppercase tracking-widest">Member Scan</span>
          </div>
        </div>
        <div className="pointer-events-auto flex gap-2">
          {availableCameras.length > 1 && !isMobile && (
            <button onClick={handleCameraSwitch} className="w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10 hover:bg-white/10 transition-all">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
            </button>
          )}
          <button onClick={stopScanning} className="w-10 h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/10 hover:bg-white/10 transition-all">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </div>

      {/* Camera / state area */}
      <div className="flex-1 relative min-h-0 flex flex-col">
        <div className="absolute inset-0 bg-black flex items-start justify-center">
          {isScanning ? (
            <QRScanner
              key={`scanner-${scannerKey}`}
              isScanning={isScanning}
              selectedCameraId={selectedCameraId}
              onQRCodeDetected={handleQRCodeDetected}
              onError={err => { setAttendanceState({ status: 'error', message: err }); setIsScanning(false); }}
              onCameraReady={() => setAttendanceState({ status: 'scanning', message: 'Scanning' })}
            />
          ) : (
            <div className="flex flex-col items-center justify-start text-center p-8 z-10 pt-20 sm:pt-32">
              {attendanceState.status === 'success' ? (
                <div className="animate-in zoom-in duration-500">
                  <div className="w-20 h-20 bg-[#AAD816] rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-[#AAD816]/20 cursor-pointer" onClick={() => setIsScanning(true)}>
                    {attendanceState.action === 'checkout'
                      ? <svg className="w-10 h-10 text-zinc-950" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                      : <svg className="w-10 h-10 text-zinc-950" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M5 13l4 4L19 7" /></svg>
                    }
                  </div>
                  <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-1">{attendanceState.action === 'checkout' ? 'Checkout' : 'Welcome!'}</h2>
                  <p className="text-3xl font-black text-[#AAD816] mb-4">{attendanceState.memberInfo?.name}</p>
                  <button onClick={startScanning} className="mt-2 px-8 py-3 bg-white/10 border border-white/20 rounded-full text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all">Scan Next</button>
                </div>
              ) : attendanceState.status === 'blocked' ? (
                <div>
                  <div className="w-20 h-20 bg-red-600 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-red-600/30">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={4} d="M6 18L18 6M6 6l12 12" /></svg>
                  </div>
                  <h2 className="text-xl font-black text-white uppercase mb-1">{attendanceState.message}</h2>
                  <p className="text-white/40 text-[10px] font-black uppercase">{attendanceState.memberInfo?.name}</p>
                  <button onClick={startScanning} className="mt-6 px-8 py-3 bg-white/10 border border-white/20 rounded-full text-white text-[10px] font-black uppercase tracking-widest hover:bg-white/20 transition-all">Try Again</button>
                </div>
              ) : attendanceState.status === 'processing' ? (
                <div className="flex flex-col items-center">
                  <div className="relative w-20 h-20 mx-auto mb-6">
                    <div className="absolute inset-0 rounded-full border-4 border-white/5" />
                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#AAD816] animate-spin" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-3 h-3 bg-[#AAD816] rounded-full animate-pulse shadow-[0_0_12px_rgba(170,216,22,0.8)]" />
                    </div>
                  </div>
                  <p className="text-white text-base font-black uppercase tracking-widest">Verifying</p>
                </div>
              ) : (
                <div className="animate-in fade-in duration-700">
                  <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-[2rem] flex items-center justify-center mx-auto mb-6 text-white/20">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 17h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                  </div>
                  <p className="text-white text-base font-black uppercase tracking-widest mb-1">Ready to Scan</p>
                  <p className="text-white/30 text-[9px] uppercase font-bold max-w-[180px] leading-tight mx-auto">Position member QR code within frame</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom scan button */}
        <div className="absolute bottom-0 left-0 right-0 z-20 flex flex-col items-center">
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/90 to-transparent pointer-events-none" />
          <div className="relative w-full flex flex-col items-center pb-8 pt-14">
            <div className="relative group mb-6">
              <button
                onClick={() => isScanning ? stopScanning() : startScanning()}
                className={`relative z-20 w-24 h-24 rounded-full flex flex-col items-center justify-center transform active:scale-95 transition-all duration-300 ${isScanning ? 'bg-red-600 shadow-2xl shadow-red-600/40 border-4 border-red-500/50' : 'bg-[#AAD816] shadow-2xl shadow-[#AAD816]/40 border-4 border-[#AAD816]/30 hover:bg-[#99C514]'}`}
              >
                {isScanning ? (
                  <><div className="w-7 h-7 bg-white rounded-lg mb-1 animate-pulse" /><span className="text-[10px] font-black text-white uppercase">Stop</span></>
                ) : (
                  <><svg className="w-10 h-10 text-zinc-950" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /></svg><span className="text-[10px] font-black text-zinc-950 uppercase mt-1">Scan</span></>
                )}
              </button>
              <div className={`absolute inset-0 z-10 -m-3 rounded-full border-2 border-white/10 transition-all duration-1000 ${isScanning ? 'animate-ping scale-150' : 'group-hover:scale-110'}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Main dashboard render ───────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-950 font-sans">
      {/* Full-screen scanner overlay — visible while scanning or showing result */}
      {(isScanning || (attendanceState.status !== 'idle' && attendanceState.status !== 'error')) && (
        <ScannerOverlay />
      )}

      {/* Dashboard */}
      <div className={isScanning ? 'hidden' : undefined}>

        {/* Premium Internal Header */}
        <div className="px-4 sm:px-6 py-8">
          <div className="bg-white p-8 rounded-[2.5rem] border border-zinc-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative group">
            <div className="absolute inset-0 overflow-hidden rounded-[2.5rem] pointer-events-none z-0">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#AAD816]/10 rounded-full blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-[#AAD816]/20"></div>
            </div>
            <div className="relative z-10">
              <h1 className="text-3xl font-black text-black tracking-tight leading-none uppercase">Attendance Hub</h1>
              <p className="text-[10px] font-black text-zinc-400 mt-2 uppercase tracking-[0.2em]">Real-time Footfall & Staff Tracking</p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 relative z-10">
              {/* Branch selector */}
              {branches.length > 0 && (
                <div className="relative">
                  <button
                    onClick={e => { e.stopPropagation(); setIsBranchMenuOpen(v => !v); }}
                    className="flex h-11 items-center gap-2 px-6 bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 rounded-xl text-zinc-950 text-[10px] font-black uppercase tracking-widest transition-all active:scale-95"
                  >
                    <svg className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="max-w-[120px] truncate">{isLoadingBranches ? 'Loading...' : selectedBranchName}</span>
                    <svg className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isBranchMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isBranchMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setIsBranchMenuOpen(false)} />
                      <div className="absolute top-full right-0 mt-2 z-50 w-52 bg-white border border-zinc-200 rounded-2xl overflow-hidden shadow-2xl">
                        <div className="py-1.5">
                          <div className="px-4 py-2 border-b border-zinc-100 mb-1">
                            <span className="text-[9px] font-black text-zinc-400 uppercase tracking-[0.3em]">Location</span>
                          </div>
                          <button
                            onClick={() => { setSelectedBranchId('all'); setIsBranchMenuOpen(false); }}
                            className={`w-full text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-between ${selectedBranchId === 'all' ? 'bg-[#AAD816] text-black' : 'text-zinc-600 hover:bg-zinc-50 hover:text-black'}`}
                          >
                            <span>All Branches</span>
                            {selectedBranchId === 'all' && <div className="w-1.5 h-1.5 bg-black rounded-full" />}
                          </button>
                          {branches.map(b => (
                            <button
                              key={b.id}
                              onClick={() => { setSelectedBranchId(b.id); setIsBranchMenuOpen(false); }}
                              className={`w-full text-left px-4 py-3 text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-between ${selectedBranchId === b.id ? 'bg-[#AAD816] text-black' : 'text-zinc-600 hover:bg-zinc-50 hover:text-black'}`}
                            >
                              <span>{b.displayName || b.name}</span>
                              {selectedBranchId === b.id && <div className="w-1.5 h-1.5 bg-black rounded-full" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowMemberModal(true)}
                  className="flex-1 sm:flex-none h-11 px-6 bg-zinc-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border border-zinc-800 flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Log Member</span>
                </button>

                {canUseStaffAttendance && (
                  <button
                    onClick={() => setShowStaffModal(true)}
                    className="flex-1 sm:flex-none h-11 px-6 bg-[#AAD816] text-black rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-[#AAD816]/20 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Log Staff</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats chips ──────────────────────────────────────────────────── */}

        {/* Mobile: vertical stack of compact horizontal tiles */}
        <div className="px-4 sm:hidden pb-4 flex flex-col gap-3">
          {[
            {
              label: 'Daily Footfall',
              sublabel: 'Total check-ins today',
              value: todayStats.total,
              icon: <TrendingUp size={22} strokeWidth={2.5} className="text-[#AAD816]" />,
              iconBg: 'bg-zinc-900',
              isLive: false,
            },
            {
              label: 'Peak Hour',
              sublabel: 'Highest traffic hour today',
              value: peakHourLabel,
              icon: <Zap size={22} strokeWidth={2.5} className="text-zinc-800" />,
              iconBg: 'bg-zinc-100 border border-zinc-200',
              isLive: false,
            },
            {
              label: 'Live Now',
              sublabel: 'Currently on-premise',
              value: isLoadingStats ? (
                <div className="h-7 w-10 bg-zinc-100 animate-pulse rounded-lg" />
              ) : todayStats.live,
              icon: (
                <div className="flex items-end gap-[3px] h-5">
                  <div className="w-1 bg-[#AAD816] rounded-full animate-wave-1 h-2" />
                  <div className="w-1 bg-[#AAD816] rounded-full animate-wave-2 h-5" />
                  <div className="w-1 bg-[#AAD816] rounded-full animate-wave-3 h-3" />
                </div>
              ),
              iconBg: 'bg-[#AAD816]/10 border border-[#AAD816]/30',
              isLive: true,
            },
          ].map(({ label, sublabel, value, icon, iconBg, isLive }) => (
            <div
              key={label}
              className="bg-white border border-zinc-100 shadow-sm rounded-2xl px-4 py-3 flex items-center justify-between gap-4 relative"
            >
              {/* Left: icon + labels */}
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                  {icon}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest leading-tight">{label}</p>
                  <p className="text-[10px] text-zinc-400 font-medium leading-tight mt-0.5 truncate">{sublabel}</p>
                </div>
              </div>
              {/* Right: big number */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {isLive && (
                  <span className="w-2 h-2 bg-[#AAD816] rounded-full animate-pulse shadow-[0_0_6px_#AAD816]" />
                )}
                <h3 className="text-3xl font-black text-zinc-900 tabular-nums tracking-tight">{value}</h3>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop / tablet: horizontal scroll carousel (original) */}
        <div className="hidden sm:flex px-4 sm:px-6 py-8 gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory">
          {[
            {
              label: 'Daily Footfall',
              value: todayStats.total,
              icon: <div className="w-12 h-12 rounded-[18px] bg-zinc-900 flex items-center justify-center text-[#AAD816] shadow-xl shadow-zinc-200"><TrendingUp size={20} strokeWidth={2.5} /></div>,
              secondary: 'Total checkins'
            },
            {
              label: 'Peak Density',
              value: peakHourLabel,
              icon: <div className="w-12 h-12 rounded-[18px] bg-zinc-100 flex items-center justify-center text-zinc-900 border border-zinc-200"><Zap size={20} strokeWidth={2.5} /></div>,
              secondary: 'Highest traffic hour'
            },
            {
              label: 'Active · Live Now',
              value: isLoadingStats ? (
                <div className="h-8 w-16 bg-zinc-100 animate-pulse rounded-xl" />
              ) : todayStats.live,
              icon: (
                <div className="w-12 h-12 rounded-[18px] bg-[#AAD816]/10 border border-[#AAD816]/30 flex items-center justify-center">
                  <div className="flex items-end gap-[3px] h-4">
                    <div className="w-1 bg-[#AAD816] rounded-full animate-wave-1 h-2" />
                    <div className="w-1 bg-[#AAD816] rounded-full animate-wave-2 h-4" />
                    <div className="w-1 bg-[#AAD816] rounded-full animate-wave-3 h-3" />
                  </div>
                </div>
              ),
              isLive: true,
              secondary: 'On-premise members'
            }
          ].map(({ label, value, icon, secondary, isLive }) => (
            <div
              key={label}
              className={`flex-shrink-0 snap-center min-w-[220px] bg-white border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-[40px] p-7 transition-all duration-500 hover:scale-[1.02] hover:shadow-[0_20px_50px_rgba(0,0,0,0.08)] relative overflow-hidden group`}
            >
              {isLive && (
                <div className="absolute top-0 right-0 p-4">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#AAD816]/10 rounded-full border border-[#AAD816]/20">
                     <span className="w-1.5 h-1.5 bg-[#AAD816] rounded-full animate-pulse shadow-[0_0_8px_#AAD816]" />
                     <span className="text-[8px] font-black text-black uppercase tracking-widest">Live</span>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-6">
                {icon}
                <div>
                   <p className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.2em] mb-1.5 group-hover:text-zinc-600 transition-colors">{label}</p>
                   <div className="flex items-baseline gap-2">
                     <h3 className="text-4xl font-black text-zinc-900 tabular-nums tracking-tighter">{value}</h3>
                     {secondary && !isLive && <span className="text-[8px] font-bold text-zinc-400 uppercase tracking-widest truncate">{secondary}</span>}
                   </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Charts ───────────────────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 pb-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* PulseTimeline – full width on mobile, 2/3 on desktop */}
          <div className="lg:col-span-2 bg-white border border-zinc-200 shadow-sm rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div className="flex-shrink-0">
                <h2 className="text-xs font-black text-black uppercase tracking-wider">Today's Hourly Traffic</h2>
                <p className="text-[9px] text-zinc-400 font-bold mt-0.5">Check-ins throughout the day</p>
              </div>

              {/* Time range picker */}
              <div className="flex items-center gap-2">
                <HourSelect
                  value={rangeStart}
                  onChange={v => {
                    setRangeStart(v);
                    if (v >= rangeEnd) setRangeEnd(Math.min(v + 1, 23));
                    if (selectedHour !== undefined && selectedHour < v) setSelectedHour(undefined);
                  }}
                  min={0}
                  max={rangeEnd - 1}
                />
                <span className="text-zinc-300 text-xs font-bold flex-shrink-0">to</span>
                <HourSelect
                  value={rangeEnd}
                  onChange={v => {
                    setRangeEnd(v);
                    if (v <= rangeStart) setRangeStart(Math.max(v - 1, 0));
                    if (selectedHour !== undefined && selectedHour > v) setSelectedHour(undefined);
                  }}
                  min={rangeStart + 1}
                  max={23}
                />
                {(rangeStart !== 6 || rangeEnd !== 21) && (
                  <button
                    onClick={() => { setRangeStart(6); setRangeEnd(21); }}
                    className="text-[9px] font-black text-zinc-400 hover:text-black uppercase tracking-widest transition-colors flex-shrink-0 px-1"
                    title="Reset to default"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
            <PulseTimeline
              data={hourlyData}
              selectedHour={selectedHour}
              onHourChange={setSelectedHour}
              hourStart={rangeStart}
              hourEnd={rangeEnd}
              isLoading={isLoadingHourly}
            />
          </div>

          {/* TimeRingHeatmap – full width on mobile, 1/3 on desktop */}
          <div className="bg-white border border-zinc-200 shadow-sm rounded-2xl p-5 flex flex-col">
            <div className="mb-4">
              <h2 className="text-xs font-black text-black uppercase tracking-wider">Today's Activity Clock</h2>
              <p className="text-[9px] text-zinc-400 font-bold mt-0.5">24-hour heatmap</p>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <TimeRingHeatmap
                data={hourlyData}
                selectedHour={selectedHour}
                onHourChange={setSelectedHour}
                isLoading={isLoadingHourly}
              />
            </div>
          </div>
        </div>

        {/* ── Average Hourly Trend ──────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 pb-4">
          <div className="bg-white border border-zinc-200 shadow-sm rounded-2xl p-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
              <div>
                <h2 className="text-sm font-black text-black uppercase tracking-wider">Peak Hour Pattern</h2>
                <p className="text-xs text-zinc-400 font-medium mt-0.5">
                  {avgTotalDays > 0
                    ? `Based on ${avgTotalDays} day${avgTotalDays !== 1 ? 's' : ''} of data — shows your busiest hours`
                    : 'Not enough data yet — come back tomorrow'}
                </p>
              </div>
              {avgTotalDays > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 rounded-full border border-violet-100">
                  <span className="w-2 h-2 bg-violet-400 rounded-full" />
                  <span className="text-[10px] font-black text-violet-600 uppercase tracking-widest">Avg</span>
                </div>
              )}
            </div>

            {isLoadingAvgHourly ? (
              <div className="h-40 flex items-center justify-center">
                <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-violet-400" />
              </div>
            ) : avgTotalDays === 0 ? (
              <div className="h-40 flex flex-col items-center justify-center gap-3 text-center">
                <span className="text-4xl">📊</span>
                <div>
                  <p className="text-sm font-bold text-zinc-500">No historical data yet</p>
                  <p className="text-xs text-zinc-400 mt-1">This chart builds automatically each day as members check in</p>
                </div>
              </div>
            ) : (() => {
              const maxVal = Math.max(...avgHourlyData, 0.01);
              const peakHour = avgHourlyData.indexOf(Math.max(...avgHourlyData));
              const peakLabel = peakHour === 0 ? '12 AM' : peakHour < 12 ? `${peakHour} AM` : peakHour === 12 ? '12 PM' : `${peakHour - 12} PM`;
              return (
                <div className="w-full">
                  {/* Peak callout banner */}
                  <div className="mb-4 mt-2 flex items-center gap-3 bg-violet-50 border border-violet-100 rounded-xl px-4 py-3">
                    <span className="text-2xl">🏆</span>
                    <div>
                      <p className="text-xs font-black text-violet-800 uppercase tracking-wide">Busiest Hour</p>
                      <p className="text-lg font-black text-violet-900">{peakLabel} — {peakHour + 1 <= 12 ? peakHour + 1 === 12 ? '12 PM' : `${peakHour + 1} AM` : `${peakHour - 11} PM`}</p>
                      <p className="text-[10px] text-violet-500 font-medium">Avg {Math.max(...avgHourlyData).toFixed(1)} check-ins/day during this hour</p>
                    </div>
                  </div>

                  {/* Bar chart — horizontally scrollable on mobile */}
                  <div className="overflow-x-auto -mx-1 px-1">
                    <div className="flex items-end gap-0.5 w-full" style={{ minWidth: 360, height: 120 }}>
                      {avgHourlyData.map((val, h) => {
                        const heightPct = (val / maxVal) * 100;
                        const isPeak = h === peakHour;
                        const isFiltered = h >= rangeStart && h <= rangeEnd;
                        // Show label every 3 hours: 12a, 3a, 6a, 9a, 12p, 3p, 6p, 9p
                        const showLabel = h % 3 === 0;
                        const labelText = h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`;
                        return (
                          <div
                            key={h}
                            className="flex flex-col items-center flex-1 group"
                            title={`${h}:00–${h + 1}:00 · avg ${val.toFixed(1)} check-ins`}
                          >
                            {/* Bar */}
                            <div className="w-full flex items-end" style={{ height: 96 }}>
                              <div
                                className={`w-full rounded-t-md transition-all duration-300 ${
                                  isPeak
                                    ? 'bg-violet-500'
                                    : isFiltered
                                    ? 'bg-violet-200 group-hover:bg-violet-400'
                                    : 'bg-zinc-100 group-hover:bg-zinc-200'
                                }`}
                                style={{ height: `${Math.max(heightPct, val > 0 ? 5 : 0)}%` }}
                              />
                            </div>
                            {/* X-axis label */}
                            <span
                              className={`mt-1 tabular-nums leading-none font-bold transition-colors ${
                                showLabel
                                  ? isPeak
                                    ? 'text-[9px] text-violet-600'
                                    : 'text-[9px] text-zinc-500'
                                  : 'text-[7px] text-zinc-300'
                              }`}
                            >
                              {showLabel ? labelText : '·'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-100 flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-violet-500" />
                      <span className="text-xs text-zinc-500 font-semibold">Busiest hour</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-violet-200" />
                      <span className="text-xs text-zinc-500 font-semibold">Your selected time range</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-zinc-100" />
                      <span className="text-xs text-zinc-500 font-semibold">Outside range</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        <div className="px-4 sm:px-6 pb-4">
          <div className="bg-white border border-zinc-200 shadow-sm rounded-2xl p-5">
            <div className="mb-4">
              <h2 className="text-xs font-black text-black uppercase tracking-wider">Attendance Trends</h2>
              <p className="text-[9px] text-zinc-400 font-bold mt-0.5">Check-ins over the past 30 days</p>
            </div>
            <div className="w-full">
              <AttendanceBarChart data={dailyTrendData} height={200} isLoading={isLoadingTrends} />
            </div>
          </div>
        </div>

        {/* ── Recent Check-ins ─────────────────────────────────────────────── */}
        <div className="px-4 sm:px-6 pb-8">
          {!isRecentLoaded ? (
            <div className="bg-white border border-zinc-200 shadow-sm rounded-2xl p-8 flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 bg-zinc-50 rounded-full flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <h3 className="text-sm font-black text-zinc-950 uppercase mb-1">Recent Activity</h3>
              <p className="text-[10px] text-zinc-400 font-bold mb-6 max-w-[200px]">Load the latest check-ins to monitor gym activity in real-time</p>
              <button
                onClick={() => setIsRecentLoaded(true)}
                className="px-6 py-2.5 bg-black text-[#AAD816] text-[10px] font-black uppercase tracking-widest rounded-full hover:bg-zinc-800 transition-all active:scale-95 shadow-lg shadow-black/10"
              >
                Load Recent Check-ins
              </button>
            </div>
          ) : (
            <div className="bg-white border border-zinc-200 shadow-sm rounded-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <div>
                <h2 className="text-xs font-black text-black uppercase tracking-wider">Recent Check-ins</h2>
                <p className="text-[9px] text-zinc-400 font-bold mt-0.5">Latest activity</p>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_6px_rgba(34,197,94,0.4)]" />
                <span className="text-[9px] font-black text-zinc-400 uppercase tracking-widest">Live</span>
              </div>
            </div>

            {isLoadingRecent ? (
              <div className="py-20 flex flex-col items-center justify-center text-zinc-300 gap-3">
                <Loader2 className="animate-spin" size={24} />
                <p className="text-[8px] font-black uppercase tracking-widest">Fetching activity...</p>
              </div>
            ) : recentCheckIns.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-zinc-300 text-xs font-black uppercase tracking-widest">No check-ins today</p>
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {recentCheckIns.slice(0, 8).map((ci, i) => {
                  const checkInDate = new Date(ci.checkInTime);
                  const isToday = checkInDate.toDateString() === new Date().toDateString();
                  return (
                    <div key={ci.id || i} className="flex items-center justify-between px-5 py-3.5 hover:bg-zinc-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-[#AAD816] rounded-xl flex items-center justify-center text-xs font-black text-black flex-shrink-0">
                          {ci.memberName?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-black text-black uppercase tracking-tight">{ci.memberName}</p>
                          <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest">{ci.branchName}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-right">
                        <div className="hidden sm:block">
                          <p className="text-[8px] font-bold text-zinc-300 uppercase mb-0.5">In</p>
                          <p className="text-sm font-black text-black tabular-nums">
                            {checkInDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div>
                          {ci.checkOutTime ? (
                            <span className="inline-flex items-center px-2.5 py-1 bg-zinc-100 border border-zinc-200 rounded-full text-[8px] font-black text-zinc-400 uppercase tracking-wider">Out</span>
                          ) : isToday ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#AAD816]/20 border border-[#AAD816]/40 rounded-full text-[8px] font-black text-black uppercase tracking-wider">
                              <span className="w-1 h-1 bg-black rounded-full animate-pulse" />Live
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-1 bg-amber-100 border border-amber-200 rounded-full text-[8px] font-black text-amber-600 uppercase tracking-wider">Missed</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>

      {/* ── Help modal ────────────────────────────────────────────────────── */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] p-6 flex items-center justify-center" {...backdropClose(() => setShowHelpModal(false))}>
          <div className="bg-white border border-zinc-200 rounded-[3rem] p-10 max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-10">
              <h3 className="text-3xl font-black text-black uppercase">How to Scan</h3>
              <p className="text-zinc-400 text-[10px] font-black uppercase mt-2 tracking-widest">Attendance Guide</p>
            </div>
            <div className="space-y-4">
              {[
                { t: 'Click Member', d: 'Tap the Member button to open the QR scanner' },
                { t: 'Scan Code', d: "Point the scanner at the member's QR code" },
                { t: 'Done', d: 'Attendance is automatically recorded' }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-5 p-5 bg-zinc-50 rounded-3xl border border-zinc-100">
                  <div className="w-10 h-10 rounded-2xl bg-[#AAD816] flex items-center justify-center text-black font-black text-xs">{i + 1}</div>
                  <div><p className="text-black font-black uppercase text-xs">{item.t}</p><p className="text-zinc-500 text-[10px] font-bold mt-0.5">{item.d}</p></div>
                </div>
              ))}
            </div>
            <button onClick={() => setShowHelpModal(false)} className="w-full mt-10 py-6 bg-black text-[#AAD816] rounded-3xl font-black uppercase text-[10px] tracking-[0.3em] hover:bg-[#AAD816] hover:text-black transition-all">Got it</button>
          </div>
        </div>
      )}

      {/* Staff modal */}
      {canUseStaffAttendance && (
        <StaffAttendanceModal isOpen={showStaffModal} onClose={() => setShowStaffModal(false)} />
      )}

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>
      {/* Member Manual Modal */}
      {showMemberModal && (
        <MemberAttendanceModal
          branchId={selectedBranchId}
          onClose={() => setShowMemberModal(false)}
          onSuccess={() => {
            fetchRecentCheckIns();
            fetchHourlyData();
          }}
        />
      )}

      {/* ── Custom Animations for Live Indicator ── */}
      <style>{`
        @keyframes wave-bar {
          0%, 100% { height: 4px; }
          50% { height: 14px; }
        }
        .animate-wave-1 { animation: wave-bar 0.8s ease-in-out infinite 0.1s; }
        .animate-wave-2 { animation: wave-bar 0.8s ease-in-out infinite 0.3s; }
        .animate-wave-3 { animation: wave-bar 0.8s ease-in-out infinite 0.5s; }
      `}</style>
    </div>
  );
}
